import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import 'features/reports/pages/reports-schedule/reports-schedule.scss';
import { ButtonColor, ButtonType, DataTable, Icon, TableColumn, useModal } from '@ampol/design-system';
import ContentControls from 'components/content-controls/content-controls';
import {
  deleteScheduledReport,
  getScheduledReport,
  updateScheduledReport,
  createScheduledReport,
  enableScheduledReport,
  disableScheduledReport,
} from 'actions/reportsActions';
import ReportsList from 'features/reports/components/reports-list/reports-list';
import PageHeader from 'components/layout/page-header/page-header';
import ReportOptionsForm from 'forms/report-options-form/report-options-form';
import Validation from 'constants/validation-messages';
import { useCheckGetAllAccountsRequest } from 'components/use-get-all-accounts-request/use-get-all-accounts-request';
import { trackCustomEvent } from 'utilities/app-insights';
import FilterControl from 'components/filter-control/filter-control';
import { selectReportsSchedulePageContext } from 'selectors/reportsScheduleSelectors';
import { useReportsScheduleAccountSelection } from 'hooks/useReportsScheduleAccountSelection';
import { useReportsSchedulePagination } from 'hooks/useReportsSchedulePagination';
import { useReportsScheduleDataLifecycle } from 'hooks/useReportsScheduleDataLifecycle';

/**
 * Reports schedule container rendering scheduled reports page
 *
 * @returns JSX Element
 */
const ReportsSchedule = () => {
  const dispatch = useDispatch();
  useCheckGetAllAccountsRequest();

  const {
    // User context
    loggedInUser,
    assignedGrandfatherClientNumber,
    assignedAccountNumber,
    canReadWrite,
    // Accounts data
    allAccounts,
    // Scheduled reports list
    scheduledReportsList,
    scheduledReportsListLoaded,
    scheduledReportsListError,
    scheduledReportsListLoading,
    scheduledReportsResultSize,
    // Single scheduled report
    scheduledReport,
    scheduledReportLoading,
    // CRUD operation completion flags
    deleteScheduledReportLoaded,
    createScheduledReportLoaded,
    updateScheduledReportLoaded,
    enableScheduledReportLoaded,
    disableScheduledReportLoaded,
    // CRUD operation loading states
    createScheduledReportLoading,
    updateScheduledReportLoading,
    enableScheduledReportLoading,
    disableScheduledReportLoading,
  } = useSelector(selectReportsSchedulePageContext);

  // Local component state
  const [selectedReportTypeId, setSelectedReportTypeId] = useState(null);
  const [reportName, setReportName] = useState(null);
  const [reportDescription, setReportDescription] = useState(null);
  const [isEditScheduledReport, setIsEditScheduledReport] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  // Lifted here so both useReportsSchedulePagination and useReportsScheduleAccountSelection
  // can share the same contract number — pagination analytics require the current selection.
  const [selectedContractNumber, setSelectedContractNumber] = useState<string | null>(null);

  // HOOK 1: Pagination, Search, and Sort management
  const pagination = useReportsSchedulePagination({
    loggedInUser,
    assignedAccountNumber,
    selectedContractNumber, // Now receives the real value, not hardcoded null
  });

  // HOOK 2: Account Selection management
  const accountSelection = useReportsScheduleAccountSelection({
    allAccounts,
    loggedInUser,
    assignedAccountNumber,
    setPageNumber: pagination.setPageNumber,
    selectedContractNumber,
    setSelectedContractNumber,
  });

  // Extract for convenient access
  const {
    searchTerm,
    sortColumn,
    sortDirection,
    pageNumber,
    pageSize,
    offset,
    setSearchTerm,
    setSortColumn,
    setSortDirection,
    setPageNumber,
    handleSearchTermChange,
    handleTableColumnSorted,
    handlePageNumberChange,
  } = pagination;

  const {
    isAccountSelected,
    selectedContractName,
    setIsAccountSelected,
    setSelectedContractName,
    handleSelectAccount,
  } = accountSelection;

  const handleRunNewReportClick = () => {
    if (isAccountSelected) {
      window.dataLayer.push({
        event: 'Card_Portal_Reports_Create_Report_Button',
        button_click: true,
      });
      trackCustomEvent(
        'Click Create Report',
        loggedInUser,
        assignedAccountNumber,
        'Clicked on Create Report button to view list of reports.',
        { selectedAccount: selectedContractNumber }
      );
      setIsDisabled(false);
      listModal.open();
    }
  };

  const createNewReportSchedule = (reportTypeId, reportName, reportDescription) => {
    setIsEditScheduledReport(false);

    trackCustomEvent(
      'Select Create Report Type',
      loggedInUser,
      assignedAccountNumber,
      'Selected a type of report schedule to create.',
      { selectedAccount: selectedContractNumber }
    );
    setSelectedReportTypeId(reportTypeId);
    setReportName(reportName);
    setReportDescription(reportDescription);
  };

  useEffect(() => {
    if (selectedReportTypeId && reportName && reportDescription) {
      reportOptionsFormModal.open();
    }
  }, [selectedReportTypeId, reportName, reportDescription]);

  const handleListModalClose = () => {
    window.dataLayer.push({
      event: 'Card_Portal_Reports_Create_Report_Exit',
      button_click: true,
    });
  };

  const listModal = useModal({
    id: 'report-list',
    title: 'New Report',
    subtitle: `${selectedContractName} ${selectedContractNumber}`,
    content: <ReportsList createNewReportSchedule={createNewReportSchedule} />,
    variant: 'triple',
    color: ButtonColor.Blue,
    onClose: handleListModalClose,
  });

  const handleSubmitReport = (request, scheduledReportId) => {
    if (scheduledReportId) {
      trackCustomEvent(
        'Submit Edit Schedule',
        loggedInUser,
        assignedAccountNumber,
        'Clicked Submit to Edit Report Schedule.',
        { selectedAccount: selectedContractNumber, reportId: scheduledReportId }
      );
      dispatch(
        updateScheduledReport(assignedGrandfatherClientNumber, selectedContractNumber, scheduledReportId, request)
      );
    } else {
      trackCustomEvent(
        'Submit Create Report',
        loggedInUser,
        assignedAccountNumber,
        'Clicked Submit to Create Report Schedule.',
        { selectedAccount: selectedContractNumber }
      );
      dispatch(createScheduledReport(assignedGrandfatherClientNumber, selectedContractNumber, request));
    }
  };

  const handleDisableReport = () => {
    trackCustomEvent(
      'Disable Report',
      loggedInUser,
      assignedAccountNumber,
      'Clicked Disable button to disable report.',
      { selectedAccount: selectedContractNumber, reportId: selectedReportTypeId }
    );
    dispatch(
      disableScheduledReport(assignedGrandfatherClientNumber, selectedContractNumber, scheduledReport.reportScheduleId)
    );
  };

  const handleEnableReport = () => {
    trackCustomEvent('Enable Report', loggedInUser, assignedAccountNumber, 'Clicked Enable button to enable report.', {
      selectedAccount: selectedContractNumber,
      reportId: selectedReportTypeId,
    });
    dispatch(
      enableScheduledReport(assignedGrandfatherClientNumber, selectedContractNumber, scheduledReport.reportScheduleId)
    );
  };

  const handleAddEditReportModalClose = () => {
    trackCustomEvent(
      'Close Add Edit Scheduled Report Modal',
      loggedInUser,
      assignedAccountNumber,
      'Closed Add Edit Scheduled Report Modal.'
    );
    setSelectedReportTypeId(null);
    setReportName(null);
    setReportDescription(null);
  };

  const formId = `report-options-form-${isEditScheduledReport ? scheduledReport?.reportTypeId : selectedReportTypeId}`;

  const reportOptionsFormModal = useModal({
    id: 'report-option-form',
    title: 'Report Options',
    subtitle: `${selectedContractName} ${selectedContractNumber}`,
    content: (
      <ReportOptionsForm
        formId={formId}
        isEdit={isEditScheduledReport}
        scheduledReport={scheduledReport}
        reportTypeId={isEditScheduledReport ? scheduledReport?.reportTypeId : selectedReportTypeId}
        reportName={isEditScheduledReport ? scheduledReport?.reportName : reportName}
        reportDescription={isEditScheduledReport ? scheduledReport?.reportDescription : reportDescription}
        onSubmit={handleSubmitReport}
        isDisabled={isDisabled}
        emailId={loggedInUser.emailId}
      />
    ),
    /* eslint-disable indent */
    topActions: isEditScheduledReport
      ? [
          {
            label: isDisabled ? 'Enable Report' : 'Disable Report',
            onClick:
              enableScheduledReportLoading || disableScheduledReportLoading || scheduledReportLoading
                ? undefined
                : isDisabled
                  ? handleEnableReport
                  : handleDisableReport,
            inactive:
              enableScheduledReportLoading ||
              disableScheduledReportLoading ||
              scheduledReportLoading ||
              updateScheduledReportLoading,
          },
        ]
      : undefined,
    /* eslint-enable indent */
    formId: formId,
    actions: [
      {
        label: 'Submit',
        type:
          isDisabled || createScheduledReportLoading || updateScheduledReportLoading
            ? ButtonType.Button
            : ButtonType.Submit,
        inactive:
          isDisabled ||
          createScheduledReportLoading ||
          updateScheduledReportLoading ||
          disableScheduledReportLoading ||
          scheduledReportLoading,
      },
      {
        label: isDisabled ? 'Close' : 'Cancel',
        onClick:
          createScheduledReportLoading || updateScheduledReportLoading
            ? undefined
            : () => reportOptionsFormModal.close(),
        inactive: createScheduledReportLoading || updateScheduledReportLoading,
      },
    ],
    color: ButtonColor.Blue,
    variant: 'triple',
    onClose: handleAddEditReportModalClose,
  });

  // HOOK 3: Data Lifecycle management - handles all data fetching effects
  // Must be called after modals are created since it needs their close functions
  useReportsScheduleDataLifecycle({
    // Redux state
    assignedGrandfatherClientNumber,
    assignedAccountNumber,
    allAccounts,
    deleteScheduledReportLoaded,
    createScheduledReportLoaded,
    updateScheduledReportLoaded,
    enableScheduledReportLoaded,
    disableScheduledReportLoaded,
    // Account selection state
    isAccountSelected,
    selectedContractNumber,
    setIsAccountSelected,
    setSelectedContractNumber,
    setSelectedContractName,
    // Pagination/search/sort state
    pageSize,
    offset,
    sortColumn,
    sortDirection,
    searchTerm,
    setPageNumber,
    setSearchTerm,
    setSortColumn,
    setSortDirection,
    // Modal state setters
    setIsDisabled,
    // Modal close functions
    closeListModal: listModal.close,
    closeReportOptionsFormModal: reportOptionsFormModal.close,
  });

  const getScheduleText = (scheduleFrequency, reportingDayOfWeek) => {
    return scheduleFrequency === 'Weekly' ? scheduleFrequency + ', on ' + reportingDayOfWeek : scheduleFrequency;
  };

  const handleEditScheduleClick = (event, scheduledReportId) => {
    event.preventDefault();
    window.dataLayer.push({
      event: 'Card_Portal_Reports_Reports_Schedule_Edit',
      button_click: true,
    });
    trackCustomEvent(
      'Click Edit Schedule',
      loggedInUser,
      assignedAccountNumber,
      'Clicked on Edit Schedule button to edit a schedule',
      { selectedAccount: selectedContractNumber, reportId: scheduledReportId }
    );
    const scheduledReport = scheduledReportsList.find((x) => x.reportScheduleID === scheduledReportId);

    setIsEditScheduledReport(true);
    reportOptionsFormModal.open();

    setIsDisabled(scheduledReport?.isDisabled);
    setSelectedReportTypeId(scheduledReport.reportTypeId);

    dispatch(getScheduledReport(assignedGrandfatherClientNumber, selectedContractNumber, scheduledReportId));
  };

  const handleDeleteScheduledReportClick = (scheduledReportId) => {
    window.dataLayer.push({
      event: 'Card_Portal_Reports_Reports_Schedule_Delete_Reports',
      button_click: true,
    });
    trackCustomEvent(
      'Click Delete Schedule',
      loggedInUser,
      assignedAccountNumber,
      'Clicked on Delete button to delete a schedule',
      { selectedAccount: selectedContractNumber, reportId: scheduledReportId }
    );
    dispatch(deleteScheduledReport(assignedGrandfatherClientNumber, selectedContractNumber, scheduledReportId));
  };

  const getColumns = () => {
    const columns: TableColumn[] = [
      {
        header: 'Report Name',
        accessorKey: 'reportName',
        defaultSorted: 'asc',
      },
      {
        header: 'Schedule',
        accessorFn: (x) => getScheduleText(x.scheduleFrequency, x.reportingDayOfWeek),
      },
      {
        header: 'Output Type',
        accessorFn: (x) => x.outputType,
      },
    ];

    if (canReadWrite) {
      columns.push({
        id: 'edit',
        accessorFn: (x) => (
          <>
            <a
              id={'edit'}
              key={x.reportScheduleID}
              href='reports-schedule#'
              className='reports-schedule__edit-schedule-link'
              onClick={(event) => {
                handleEditScheduleClick(event, x.reportScheduleID);
              }}
            >
              Edit Schedule
            </a>
          </>
        ),
      });

      columns.push({
        id: 'action',
        accessorFn: (x) => (
          <>
            <button
              id={'delete'}
              className={'reports-schedule__delete'}
              onClick={() => handleDeleteScheduledReportClick(x.reportScheduleID)}
            >
              <Icon url={'/icons/bin.svg'} />
            </button>
          </>
        ),
      });
    }

    return columns;
  };

  return (
    <>
      <PageHeader
        showAccountSelector={true}
        accounts={allAccounts}
        isAccountsFlatList={true}
        onAccountSelected={handleSelectAccount}
        selectedAccountNumber={selectedContractNumber}
        showButton={canReadWrite}
        buttonLabel='Create Report'
        onButtonClick={handleRunNewReportClick}
        isButtonInactive={!isAccountSelected}
      />
      <FilterControl
        showFilterControl={false}
        showSearch={true}
        defaultSearchValue={searchTerm}
        handleSearch={handleSearchTermChange}
      />
      <ContentControls
        showPageControls={isAccountSelected}
        showSearch={false}
        handleSearch={handleSearchTermChange}
        pageNumber={pageNumber}
        itemCount={scheduledReportsResultSize}
        onPageChange={handlePageNumberChange}
        isSearchOnly={true}
        defaultSearchValue={searchTerm}
      />
      <div className='reports-schedule__table-container'>
        <DataTable
          caption={'Reports options'}
          columns={getColumns()}
          isLoading={scheduledReportsListLoading}
          columnTemplate={canReadWrite ? '3fr 1fr 1fr 1fr 70px' : '3fr 1fr 1fr'}
          onColumnSorted={handleTableColumnSorted}
          data={scheduledReportsList}
          tableVariant='list'
          noDataMessage={
            scheduledReportsListLoaded
              ? Validation.NO_CONTENT
              : scheduledReportsListError
                ? Validation.CONTENT_ERROR
                : Validation.SELECT_REPORTS_ACCOUNT
          }
          rowClassFn={(rowData) => (rowData.isDisabled ? 'reports-schedule__disabled-table-row' : '')}
        />
      </div>
    </>
  );
};

export default ReportsSchedule;
